<template>
  <div class="billing-processing-panel">
    <!--begin: ERROR ALERT-->
    <b-alert v-if="errorAlert" variant="warning" show>
      <div class="text-dark" v-html="errorAlert"></div>
    </b-alert>
    <!--end: ERROR ALERT-->

    <b-alert v-if="brandLaneError" variant="warning" show class="mb-5">
      <div class="text-dark">{{ brandLaneError }}</div>
    </b-alert>

    <!--begin: INSIGHT connection — Fuse MSI INSIGHT path only-->
    <insight-connection-panel
      v-if="resolvedLedgerRowId && isFuseBrandLane"
      :ledger-row-id="resolvedLedgerRowId"
    />
    <!--end: INSIGHT connection panel-->

    <!--begin: Stamp commercial terms (Neon bound snapshot under FUSE-* only)-->
    <commercial-terms-stamp-rail
      v-if="resolvedLedgerRowId && isFuseStampCarrier"
      :ledger-row-id="resolvedLedgerRowId"
      :carrier-number="postPolicyNumber"
      :workflow-name="policyWorkflowName"
      :premium-cents="stampPremiumCents"
      @stamped="onCommercialTermsStamped"
    />
    <!--end: Stamp commercial terms-->

    <!--begin: Documents-only schedule rail — Fuse MSI (available before and after posting)-->
    <schedule-documents-rail
      v-if="resolvedLedgerRowId && isFuseBrandLane"
      :ledger-row-id="resolvedLedgerRowId"
      :policy-number="postPolicyNumber"
      :workflow-name="policyWorkflowName"
      :expected-snapshot-hash="postExpectedSnapshotHash"
      :workflow-transaction-type="documentsWorkflowTransactionType"
      :identity-ready="documentsIdentityReady"
      :has-insight-draft="postingRailHasDraft"
      :draft-insight-transaction-id="postingRailInsightTxnId"
      @focus-posting-rail="focusPostingRail"
    />
    <!--end: Documents-only schedule rail-->

    <!--begin: Interim Manual IBAIS — Rentsure / CRI only-->
    <manual-ibais-processing-rail
      v-if="resolvedLedgerRowId && isManualIbaisLane"
      :ledger-row-id="resolvedLedgerRowId"
      :brand-key="reviewBrandKey"
    />
    <!--end: Manual IBAIS-->

    <!--begin: Case evidence readiness panel (W1D) - read-only; no action controls-->
    <evidence-readiness-panel
      v-if="resolvedLedgerRowId && isFuseBrandLane"
      :ledger-row-id="resolvedLedgerRowId"
    />
    <!--end: Case evidence readiness panel-->

    <!--begin: policy-processing-readiness.v1 — single next-step truth-->
    <processing-readiness-banner
      v-if="resolvedLedgerRowId && isFuseBrandLane"
      :readiness="processingReadiness"
    />
    <!--end: policy-processing-readiness.v1-->

    <!--begin: Pre-post assemble checks (read-only)-->
    <assemble-inspect-panel
      v-if="resolvedLedgerRowId && isFuseBrandLane"
      :billing-line-id="resolvedLedgerRowId"
      @inspect-loaded="onInspectLoaded"
    />
    <!--end: Pre-post assemble checks-->

    <div v-if="review">
      <!--begin: Status banner-->
      <div class="d-flex justify-content-between align-items-center mb-5">
        <div>
          <span class="font-size-h4 font-weight-bold text-dark">
            {{ policyIdentityHeading }}
          </span>
          <span class="text-muted ml-3">
            {{ review.policy.client }} &middot; {{ review.policy.insurer }}
          </span>
          <div class="text-muted font-size-sm">
            {{ review.transactionType }} &middot; {{ review.policy.period }}
            <span v-if="review.lastValidatedAt">
              &middot; last validated {{ formattedLastValidated }}
            </span>
          </div>
          <div v-if="policyIdentityDetail" class="text-muted font-size-sm">
            {{ policyIdentityDetail }}
          </div>
          <div v-if="review.debtor && review.debtor.label" class="text-dark font-size-sm mt-1">
            Debtor: <strong>{{ review.debtor.label }}</strong>
          </div>
        </div>
        <div>
          <b-badge v-if="isPosted" variant="success" class="font-size-lg p-3">
            POSTED
          </b-badge>
          <b-badge v-else variant="warning" class="font-size-lg p-3">
            UNPOSTED
          </b-badge>
        </div>
      </div>
      <!--end: Status banner-->

      <!--begin: Exception / repair banner-->
      <b-alert v-if="!allTrustsPass && !isPosted" variant="warning" show>
        <div class="text-dark">
          <strong>This transaction cannot be posted yet.</strong>
          The following pre-post check(s) failed:
          <ul class="mb-2 mt-2">
            <li v-for="(failure, index) in failedTrusts" :key="index">
              <strong>{{ failure.label }}:</strong> {{ failure.detail }}
            </li>
          </ul>
          <button class="btn btn-sm btn-outline-primary font-weight-bold" @click.prevent="returnToFix">
            <i class="fa fa-arrow-left"></i>
            Return to fix
          </button>
        </div>
      </b-alert>
      <!--end: Exception / repair banner-->

      <div class="row">
        <div class="col-lg-6 mb-5">
          <premium-card :premium="review.premium" />
        </div>
        <div class="col-lg-6 mb-5">
          <money-movements :money="review.money" :ar-split="review.arSplit" />
        </div>
      </div>

      <div class="mb-5">
        <state-premium-table :premium-by-state="review.premiumByState" />
      </div>

      <div class="mb-5">
        <ar-split-table
          :ar-split="review.arSplit"
          :commission-config="review.commissionConfig"
        />
      </div>

      <div class="mb-5">
        <relationship-strip
          :review="review"
          :source-links="sourceLinks"
        />
      </div>

      <div v-if="isFuseBrandLane" class="mb-5">
        <write-plan-panel :write-plan="writePlan" />
      </div>

      <div v-if="isFuseBrandLane" class="mb-5">
        <trust-checklist :trusts="review.trusts" />
      </div>

      <!--begin: Draft → schedule → post rail (Fuse MSI only)-->
      <posting-rail
        ref="postingRail"
        v-if="resolvedLedgerRowId && isFuseBrandLane && !isPosted"
        class="posting-rail"
        data-posting-rail
        :ledger-row-id="resolvedLedgerRowId"
        :trusts-pass="allTrustsPass"
        :recoverable-assembly-refusal="recoverableAssemblyRefusal"
        :create-lane-next-step="createLaneNextStep"
        :is-posted="isPosted"
        :policy-number="postPolicyNumber"
        :expected-snapshot-hash="postExpectedSnapshotHash"
        :readiness-loading="processingReadinessLoading"
        @status-changed="onPostingStatusChanged"
      />
      <!--end: Draft → schedule → post rail-->

      <!--begin: Post gate (Fuse MSI only)-->
      <div
        v-if="isFuseBrandLane"
        class="d-flex justify-content-center border-top pt-8"
      >
        <post-gate
          :armed="postArmed"
          :status="postGateStatus"
          :policy-number="postPolicyNumber"
          :expected-money="postExpectedMoney"
          :expected-debtor-id="postExpectedDebtorId"
          :debtor-label="postDebtorLabel"
          :accounting-month="postAccountingMonth"
          :ledger-row-id="resolvedLedgerRowId"
          :billing-line-id="resolvedLedgerRowId"
          :invoice-number="postInvoiceNumber"
          :commission-config="review.commissionConfig"
          :ar-split="review.arSplit"
          :annual-payable="review.premium && review.premium.annualPayable"
          :underwriting-base="review.premium && review.premium.underwritingBase"
          :attempt-id="postAttemptId"
          :processing-readiness="processingReadiness"
          :readiness-loading="processingReadinessLoading"
          :schedule-attached="scheduleAttached"
          :has-draft="postingRailHasDraft"
          @posted="onPosted"
          @outcome-unknown="onOutcomeUnknown"
          @status-progress="onPostStatusProgress"
        />
      </div>
      <!--end: Post gate-->

      <!--begin: Repair route stub (v1) -->
      <b-modal
        ref="returnToFixModal" title="Return to fix" size="md"
        no-close-on-backdrop hide-header-close ok-only ok-title="Understood"
      >
        <b-card-text>
          The repair path routes back to the upstream pricing / assembly step,
          carrying the specific error, then forces a fresh INSIGHT dry-run on
          return. That routing is wired in a later slice.
        </b-card-text>
      </b-modal>
      <!--end: Repair route stub-->
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { getBillingReview } from "@/services/billing-review-service";
import PremiumCard from "./components/PremiumCard.vue";
import StatePremiumTable from "./components/StatePremiumTable.vue";
import ArSplitTable from "./components/ArSplitTable.vue";
import MoneyMovements from "./components/MoneyMovements.vue";
import TrustChecklist from "./components/TrustChecklist.vue";
import PostGate from "./components/PostGate.vue";
import PostingRail from "./components/PostingRail.vue";
import InsightConnectionPanel from "./components/InsightConnectionPanel.vue";
import CommercialTermsStampRail from "./components/CommercialTermsStampRail.vue";
import ManualIbaisProcessingRail from "./components/ManualIbaisProcessingRail.vue";
import ScheduleDocumentsRail from "./components/ScheduleDocumentsRail.vue";
import EvidenceReadinessPanel from "./components/EvidenceReadinessPanel.vue";
import AssembleInspectPanel from "./components/AssembleInspectPanel.vue";
import ProcessingReadinessBanner from "./components/ProcessingReadinessBanner.vue";
import WritePlanPanel from "./components/WritePlanPanel.vue";
import RelationshipStrip from "./components/RelationshipStrip.vue";
import {
  getInsightConnectionStatus,
  getPolicyProcessingReadiness
} from "@/services/workbench-service";

export default {
  name: "BillingProcessingPanel",

  components: {
    PremiumCard,
    StatePremiumTable,
    ArSplitTable,
    MoneyMovements,
    TrustChecklist,
    PostGate,
    PostingRail,
    InsightConnectionPanel,
    CommercialTermsStampRail,
    ManualIbaisProcessingRail,
    ScheduleDocumentsRail,
    EvidenceReadinessPanel,
    AssembleInspectPanel,
    ProcessingReadinessBanner,
    WritePlanPanel,
    RelationshipStrip
  },

  props: {
    /** K_IbaisTransactions ledger-row id when known (preferred for PostingRail). */
    ledgerRowId: {
      type: String,
      default: null
    },
    /** K_Transactions id — used when ledger is unresolved or for bare txn review. */
    transactionId: {
      type: String,
      default: null
    },
    /** Amber banner from server ledger resolution ambiguity (never guess a row). */
    resolutionError: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      review: null,
      errorAlert: null,
      loading: false,
      resolvedDebtorId: null,
      producingBrokerLabel: null,
      localPostStatus: null,
      localInvoiceNumber: null,
      scheduleAttached: false,
      postingRailHasDraft: false,
      postingRailInsightTxnId: "",
      connectionIdentityReady: false,
      processingReadiness: null,
      processingReadinessLoading: false,
      writePlan: null,
      postAttemptId: null,
      sourceLinks: [],
      stampedSnapshotHash: null
    };
  },

  mounted() {
    if (this.resolutionError && !this.ledgerRowId) {
      this.errorAlert = this.resolutionError;
      return;
    }
    this.loadReview();
  },

  watch: {
    ledgerRowId: function() {
      this.stampedSnapshotHash = null;
      this.loadReview();
    },
    transactionId: function() {
      this.stampedSnapshotHash = null;
      this.loadReview();
    },
    resolutionError: function(value) {
      if (value && !this.ledgerRowId && !this.review) {
        this.errorAlert = value;
      }
    }
  },

  computed: {
    reviewBrandKey: function() {
      return (this.review && this.review.brandKey) || "";
    },

    isFuseBrandLane: function() {
      return this.reviewBrandKey === "fuse";
    },

    isManualIbaisLane: function() {
      return (
        this.reviewBrandKey === "rentsure" || this.reviewBrandKey === "cri"
      );
    },

    brandLaneError: function() {
      if (!this.review) return "";
      if (this.review.brandError) return String(this.review.brandError);
      if (!this.reviewBrandKey) {
        return "Brand is missing on the policy/transaction. Set K_Brand (Fuse / Rentsure / CRI) so Processing can open the correct lane.";
      }
      if (!this.isFuseBrandLane && !this.isManualIbaisLane) {
        return (
          "Brand '" +
          (this.review.brand || this.reviewBrandKey) +
          "' has no Processing lane in this cut. Fuse uses INSIGHT posting; Rentsure/CRI use Manual IBAIS."
        );
      }
      return "";
    },

    resolvedLedgerRowId: function() {
      if (this.ledgerRowId) {
        return String(this.ledgerRowId);
      }
      return null;
    },

    reviewLookupId: function() {
      if (this.resolvedLedgerRowId) {
        return `ledger-${this.resolvedLedgerRowId}`;
      }
      return this.transactionId || null;
    },

    isPosted: function() {
      const st = String(this.localPostStatus || "").toLowerCase();
      if (
        st === "posted"
        || st === "confirmed"
        || st === "post_confirmed"
        || st === "neon_recorded"
        || st === "zoho_recorded"
        || st === "reconciled"
        || st.indexOf("reconciliation_hold") >= 0
      ) {
        return true;
      }
      return this.review && this.review.status === "posted";
    },

    allTrustsPass: function() {
      if (!this.review) {
        return false;
      }
      const t = this.review.trusts;
      const stateOk = !t.statePremium || t.statePremium.pass;
      const vehicleOk = !t.vehicleAllocation || t.vehicleAllocation.pass;
      return t.insightValid.pass && t.reconciled.pass && t.splitIntegrity.pass && stateOk && vehicleOk;
    },

    createLaneNextStep: function() {
      const valid = this.review && this.review.trusts && this.review.trusts.insightValid;
      const detail = valid && valid.detail;
      if (!detail || valid.pass) {
        return "";
      }
      return /insurer change|Renewal Transfer/i.test(String(detail)) ? String(detail) : "";
    },

    /** Orphan renewal period — Create draft is reachable with Recovery acknowledgement. */
    recoverableAssemblyRefusal: function() {
      return (
        !!this.review &&
        this.review.assemblyRefusalCode === "renewal_orphan_period"
      );
    },

    postArmed: function() {
      // Sole post arming signal: policy-processing-readiness.v1 review-post.
      const readiness = this.processingReadiness || {};
      if (
        readiness.contractVersion !== "policy-processing-readiness.v1"
        || readiness.nextCommand !== "review-post"
        || readiness.error
        || readiness.holdClass === "outcome_unknown"
        || readiness.holdClass === "reconciliation_hold"
      ) {
        return false;
      }
      return (
        this.allTrustsPass &&
        !this.isPosted &&
        this.postingRailHasDraft &&
        this.scheduleAttached
      );
    },

    failedTrusts: function() {
      if (!this.review) {
        return [];
      }
      const t = this.review.trusts;
      const failures = [];
      if (!t.insightValid.pass) {
        failures.push({ label: "INSIGHT valid", detail: t.insightValid.detail });
      }
      if (!t.reconciled.pass) {
        failures.push({ label: "Reconciled to source", detail: t.reconciled.detail });
      }
      if (!t.splitIntegrity.pass) {
        failures.push({ label: "Split integrity", detail: t.splitIntegrity.detail });
      }
      if (t.statePremium && !t.statePremium.pass) {
        failures.push({ label: "State premium", detail: t.statePremium.detail });
      }
      if (t.vehicleAllocation && !t.vehicleAllocation.pass) {
        failures.push({ label: "Vehicle allocation", detail: t.vehicleAllocation.detail });
      }
      return failures;
    },

    formattedLastValidated: function() {
      if (!this.review || !this.review.lastValidatedAt) {
        return "";
      }
      return moment(this.review.lastValidatedAt)
        .tz("Australia/Sydney")
        .format("DD/MM/YYYY HH:mm");
    },

    /** Fuse agency / carrier policy number (FUSE-*) for Desk Generate, Stamp, Post confirm. */
    postPolicyNumber: function() {
      const policy = this.review && this.review.policy;
      if (!policy) return "";
      // Trust server policy.number (Fuse prefers FUSE-*; non-Fuse keeps workflow Name).
      const number = policy.number != null ? String(policy.number).trim() : "";
      if (number) {
        if (/^FUSE-\d{1,12}$/i.test(number)) return number.toUpperCase();
        return number;
      }
      const carrier =
        policy.carrierNumber != null ? String(policy.carrierNumber).trim() : "";
      return carrier || "";
    },

    isFuseStampCarrier: function() {
      return /^FUSE-\d{1,12}$/i.test(String(this.postPolicyNumber || ""));
    },

    policyWorkflowName: function() {
      const policy = this.review && this.review.policy;
      if (!policy) return "";
      if (policy.workflowName) return String(policy.workflowName).trim();
      const number = policy.number != null ? String(policy.number).trim() : "";
      return /^POL-/i.test(number) ? number : "";
    },

    policyIdentityHeading: function() {
      return this.postPolicyNumber || this.policyWorkflowName || "";
    },

    policyIdentityDetail: function() {
      const fuse = this.postPolicyNumber;
      const workflow = this.policyWorkflowName;
      if (fuse && workflow && fuse !== workflow) {
        return `Workflow ${workflow} · Policy ${fuse}`;
      }
      if (fuse && !workflow) return `Policy ${fuse}`;
      if (workflow && !fuse) return `Workflow ${workflow} (Fuse policy number missing)`;
      return "";
    },

    stampPremiumCents: function() {
      const premium = this.review && this.review.premium;
      if (!premium) return null;
      const payable = premium.annualPayable != null ? Number(premium.annualPayable) : null;
      if (payable != null && Number.isFinite(payable)) {
        return Math.round(payable * 100);
      }
      const gross = premium.gross != null ? Number(premium.gross) : null;
      if (gross != null && Number.isFinite(gross)) {
        return Math.round(gross * 100);
      }
      return null;
    },

    postExpectedSnapshotHash: function() {
      // Insight Post expects the Insight/billing snapshot hash — never the Neon
      // commercial-terms content hash from Stamp.
      return (this.review && this.review.expectedSnapshotHash) || "";
    },

    documentsWorkflowTransactionType: function() {
      return (this.review && this.review.transactionType) || "";
    },

    documentsIdentityReady: function() {
      const reviewPolicy = this.review && this.review.policy;
      const reviewHasPolicyAnchor = !!(
        (this.review && this.review.insightPolicyId) ||
        (reviewPolicy && reviewPolicy.insightId)
      );
      const reviewWasValidated = !!(
        this.review &&
        this.review.trusts &&
        this.review.trusts.insightValid &&
        this.review.trusts.insightValid.pass
      );
      return this.connectionIdentityReady || reviewHasPolicyAnchor || reviewWasValidated;
    },

    postExpectedMoney: function() {
      // Confirm payable money (client receivable / annual payable), not underwriting base alone.
      if (this.review && this.review.money && this.review.money.clientReceivable != null) {
        return this.review.money.clientReceivable;
      }
      if (this.review && this.review.premium && this.review.premium.annualPayable != null) {
        return this.review.premium.annualPayable;
      }
      return null;
    },

    postExpectedDebtorId: function() {
      if (this.review && this.review.debtor && this.review.debtor.authorisedRepId != null) {
        return this.review.debtor.authorisedRepId;
      }
      if (this.resolvedDebtorId != null && this.resolvedDebtorId !== "") {
        return this.resolvedDebtorId;
      }
      if (this.review && this.review.expectedDebtorId != null) {
        return this.review.expectedDebtorId;
      }
      if (this.review && this.review.debtorId != null) {
        return this.review.debtorId;
      }
      const ar1 =
        this.review &&
        Array.isArray(this.review.arSplit) &&
        this.review.arSplit[0];
      return (ar1 && ar1.authorisedRepId != null) ? ar1.authorisedRepId : null;
    },

    postDebtorLabel: function() {
      if (this.review && this.review.debtor && this.review.debtor.label) {
        return this.review.debtor.label;
      }
      if (this.producingBrokerLabel) {
        const id = this.postExpectedDebtorId;
        return id != null
          ? `${this.producingBrokerLabel} (INSIGHT AR ${id})`
          : this.producingBrokerLabel;
      }
      if (this.review && this.review.producingBrokerName) {
        return this.review.producingBrokerName;
      }
      const ar1 =
        this.review &&
        Array.isArray(this.review.arSplit) &&
        this.review.arSplit[0];
      return (ar1 && (ar1.roleLabel || ar1.role)) || "Producing broker";
    },

    postAccountingMonth: function() {
      if (this.review && this.review.accountingMonth) {
        return this.review.accountingMonth;
      }
      return "";
    },

    postInvoiceNumber: function() {
      if (this.localInvoiceNumber != null) {
        return this.localInvoiceNumber;
      }
      if (this.review && this.review.invoiceNumber != null) {
        return this.review.invoiceNumber;
      }
      return null;
    },

    postGateStatus: function() {
      if (this.localPostStatus) {
        return this.localPostStatus;
      }
      return this.review ? this.review.status : "unposted";
    }
  },

  methods: {
    loadReview: function() {
      const lookupId = this.reviewLookupId;
      this.connectionIdentityReady = false;
      if (!lookupId) {
        if (this.resolutionError) {
          this.errorAlert = this.resolutionError;
        } else {
          this.errorAlert = "No ledger row or transaction id was provided for billing review.";
        }
        return;
      }

      this.loading = true;
      this.$emit("loading", true);
      getBillingReview(lookupId)
        .then(model => {
          if (model && model.resolutionError) {
            this.errorAlert = model.resolutionError;
            this.review = null;
            this.loading = false;
            this.$emit("loading", false);
            return;
          }
          this.errorAlert = null;
          this.review = model;
          this.sourceLinks = this.buildSourceLinks(model);
          this.$emit("review-loaded", model);
          this.loading = false;
          this.$emit("loading", false);
          this.loadDebtorContext();
          this.loadProcessingReadiness();
        })
        .catch(() => {
          this.errorAlert = "Unable to load the billing review for this transaction.";
          this.loading = false;
          this.$emit("loading", false);
        });
    },

    loadProcessingReadiness: function() {
      if (!this.resolvedLedgerRowId || typeof getPolicyProcessingReadiness !== "function") {
        this.processingReadiness = null;
        this.processingReadinessLoading = false;
        return;
      }
      const ledgerRowId = this.resolvedLedgerRowId;
      const requestSeq = (this._readinessRequestSeq = (this._readinessRequestSeq || 0) + 1);
      this.processingReadinessLoading = true;
      getPolicyProcessingReadiness(ledgerRowId)
        .then((result) => {
          if (requestSeq !== this._readinessRequestSeq
            || String(this.resolvedLedgerRowId || "") !== String(ledgerRowId)) {
            return;
          }
          if (!result || result.error) {
            this.processingReadiness = null;
            this.processingReadinessLoading = false;
            return;
          }
          this.processingReadiness = result;
          this.processingReadinessLoading = false;
        })
        .catch(() => {
          if (requestSeq === this._readinessRequestSeq
            && String(this.resolvedLedgerRowId || "") === String(ledgerRowId)) {
            this.processingReadiness = null;
            this.processingReadinessLoading = false;
          }
        });
    },

    buildSourceLinks: function(model) {
      const links = [];
      if (!model || !model.policy) {
        return links;
      }
      if (model.policy.clientId) {
        links.push({
          label: "Zoho client " + (model.policy.client || ""),
          detail: String(model.policy.clientId),
          href: null
        });
      }
      if (model.policy.id) {
        links.push({
          label: "Zoho policy " + (model.policy.number || ""),
          detail: String(model.policy.id),
          href: null
        });
      }
      if (model.debtor && model.debtor.authorisedRepId != null) {
        links.push({
          label: model.debtor.label || "Producing broker",
          detail: "INSIGHT AR " + model.debtor.authorisedRepId,
          href: null
        });
      }
      if (model.transactionId) {
        links.push({
          label: "Zoho transaction",
          detail: String(model.transactionId),
          href: null
        });
      }
      return links;
    },

    loadDebtorContext: function() {
      if (!this.resolvedLedgerRowId) {
        return;
      }
      getInsightConnectionStatus(this.resolvedLedgerRowId)
        .then(connection => {
          if (!connection || connection.error || !connection.found) {
            return;
          }
          this.connectionIdentityReady = !!(
            connection.client &&
            connection.client.light === "green" &&
            connection.broker &&
            connection.broker.light === "green" &&
            connection.policy &&
            connection.policy.light === "green"
          );
          if (connection.broker && connection.broker.id != null) {
            this.resolvedDebtorId = connection.broker.id;
          } else if (
            this.resolvedDebtorId == null &&
            connection.client &&
            connection.client.id != null
          ) {
            this.resolvedDebtorId = connection.client.id;
          }
          if (connection.broker && connection.broker.name) {
            this.producingBrokerLabel = connection.broker.name;
          }
        })
        .catch(() => {
          // Non-blocking — PostGate stays disarmed for confirm until a debtor id is known.
        });
    },

    onInspectLoaded: function(payload) {
      if (payload && payload.expectedDebtorId != null) {
        this.resolvedDebtorId = payload.expectedDebtorId;
      }
    },

    focusPostingRail: function() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector
          ? this.$el.querySelector(".posting-rail, [data-posting-rail], #posting-rail")
          : null;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (this.$refs.postingRail && typeof this.$refs.postingRail.$el !== "undefined") {
          const rail = this.$refs.postingRail.$el;
          if (rail && typeof rail.scrollIntoView === "function") {
            rail.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    },

    onCommercialTermsStamped: function(payload) {
      const hash = payload && payload.contentHash != null
        ? String(payload.contentHash).trim()
        : "";
      this.stampedSnapshotHash = hash;
    },

    onPostingStatusChanged: function(payload) {
      if (!payload) return;
      this.scheduleAttached = !!payload.scheduleAttached;
      this.postingRailHasDraft = !!payload.hasDraft;
      const rawTxn =
        payload.transactionId != null
          ? payload.transactionId
          : payload.insightTransactionId != null
            ? payload.insightTransactionId
            : payload.status && payload.status.transactionId != null
              ? payload.status.transactionId
              : "";
      this.postingRailInsightTxnId = /^[1-9]\d*$/.test(String(rawTxn).trim())
        ? String(rawTxn).trim()
        : "";
      if (payload.status) {
        if (payload.status.writePlan) {
          this.writePlan = payload.status.writePlan;
        }
        if (payload.status.attemptId || payload.status.postingAttemptId) {
          this.postAttemptId = payload.status.attemptId || payload.status.postingAttemptId;
        }
        if (payload.status.status) {
          const st = String(payload.status.status).toLowerCase();
          if (st.indexOf("reconciliation_hold") >= 0) {
            this.localPostStatus = "reconciliation_hold";
          } else if (
            st === "posted"
            || st === "confirmed"
            || st === "post_confirmed"
            || st === "neon_recorded"
            || st === "zoho_recorded"
            || st === "reconciled"
          ) {
            this.localPostStatus = st === "post_confirmed"
              || st === "neon_recorded"
              || st === "zoho_recorded"
              ? "reconciliation_hold"
              : "posted";
          } else if (st === "outcome_unknown" || st === "sending" || st === "post_outcome_unknown") {
            this.localPostStatus = "outcome_unknown";
          }
        }
      }
      // Draft/schedule progress changes readiness nextCommand — reload the sole evaluator
      // so PostGate can open without a full page reload.
      this.loadProcessingReadiness();
    },

    onPostStatusProgress: function(payload) {
      if (!payload) return;
      if (payload.writePlan) {
        this.writePlan = payload.writePlan;
      }
      if (payload.attemptId) {
        this.postAttemptId = payload.attemptId;
      }
      if (payload.status) {
        const st = String(payload.status).toLowerCase();
        if (
          st.indexOf("reconciliation_hold") >= 0
          || st === "post_confirmed"
          || st === "neon_recorded"
          || st === "zoho_recorded"
        ) {
          this.localPostStatus = "reconciliation_hold";
        } else if (st.indexOf("outcome_unknown") >= 0) {
          this.localPostStatus = "outcome_unknown";
        } else {
          this.localPostStatus = st;
        }
      }
      this.loadProcessingReadiness();
    },

    onPosted: function(result) {
      const st = String((result && result.status) || "").toLowerCase();
      // Keep the "finish records" retry visible after confirmed post / Neon-only progress.
      if (
        st.indexOf("reconciliation_hold") >= 0
        || st === "post_confirmed"
        || st === "neon_recorded"
        || st === "zoho_recorded"
      ) {
        this.localPostStatus = "reconciliation_hold";
      } else if (st.indexOf("outcome_unknown") >= 0) {
        this.localPostStatus = "outcome_unknown";
      } else {
        this.localPostStatus = "posted";
      }
      if (result && (result.invoiceNumber || result.invoice)) {
        this.localInvoiceNumber = result.invoiceNumber || result.invoice;
      }
      if (result && result.writePlan) {
        this.writePlan = result.writePlan;
      }
      if (result && (result.attemptId || result.postingAttemptId)) {
        this.postAttemptId = result.attemptId || result.postingAttemptId;
      }
      if (this.review && this.localPostStatus === "posted") {
        this.review.status = "posted";
      }
      this.loadProcessingReadiness();
    },

    onOutcomeUnknown: function() {
      this.localPostStatus = "outcome_unknown";
      this.loadProcessingReadiness();
    },

    returnToFix: function() {
      this.$refs.returnToFixModal.show();
    }
  }
};
</script>
